import type { TooltipProps } from 'antd';
import { Tooltip } from 'antd';
import {
  cloneElement,
  isValidElement,
  type MouseEvent,
  type ReactElement,
  useState,
} from 'react';

function isTouchLikeDevice() {
  if (typeof window === 'undefined') return false;
  const nav = window.navigator;
  return Boolean(
    nav.maxTouchPoints > 0 ||
      window.matchMedia?.('(hover: none), (pointer: coarse)').matches,
  );
}

/**
 * Tooltip that does not steal the first tap on phones/tablets.
 *
 * Desktop keeps normal hover tooltip. On touch-like devices we render the child
 * directly so the first tap triggers the actual button instead of opening a
 * tooltip/focus state first.
 */
export function TouchSafeTooltip({
  children,
  title,
  open,
  onOpenChange,
  ...props
}: TooltipProps): ReactElement {
  const [internalOpen, setInternalOpen] = useState(false);

  if (!title || isTouchLikeDevice()) {
    return children as ReactElement;
  }

  const isControlled = open !== undefined;
  const mergedOpen = isControlled ? open : internalOpen;
  const handleOpenChange = (nextOpen: boolean) => {
    if (!isControlled) {
      setInternalOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
  };

  const closeTooltip = () => {
    if (!isControlled) {
      setInternalOpen(false);
    }
    onOpenChange?.(false);
  };

  const child = isValidElement(children)
    ? cloneElement(children as ReactElement<Record<string, unknown>>, {
        onClick: (event: MouseEvent<HTMLElement>) => {
          closeTooltip();
          const originalOnClick = (
            children.props as {
              onClick?: (event: MouseEvent<HTMLElement>) => void;
            }
          ).onClick;
          originalOnClick?.(event);
        },
      })
    : children;

  return (
    <Tooltip
      title={title}
      open={mergedOpen}
      onOpenChange={handleOpenChange}
      destroyOnHidden
      {...props}
    >
      {child}
    </Tooltip>
  );
}
